import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  BET_LIST_BET_TYPES,
  BET_LIST_BET_TYPES_ALL,
  BET_LIST_FREE_BET_TYPES,
  BET_LIST_IS_TEST_ENUM,
  BET_LIST_LIVE_PREMATCH_EVENT_TYPES_ENUM,
  BETBUILDER_OPTIONS,
  BetListFilterModel,
  BetListRow,
  BetTypesEnum,
  CASHED_OUT_VALUES,
  FreeBetResponseType,
  IS_FIRST_PLAYER_BET_ENUM,
  YES_NO_VALUES,
} from '../pages/bets/types/betListTypes';
import { PROVIDER_TYPES } from '../types/providerTypes';
import { LabelValueType } from '../components/AsyncFormSelect/AsyncFormSelect';

interface BetListState {
  bets: BetListRow[];
  isAutoEnabled: boolean;
  filter: {
    playerID?: number | string;
    playerLogin?: string;
    eventID?: number | string;
    sport?: LabelValueType;
    tournament?: LabelValueType;
    type?: LabelValueType | null;
    betBuilder?: LabelValueType;
    legs?: number;
    isTest?: LabelValueType;
    barcode?: string;
    betID?: string;
    sportsIds?: LabelValueType[];
    tournamentIds?: LabelValueType[];
    maxStakePercent?: number;
    isPartiallyCashedOut?: LabelValueType;
    BetShopBonus?: LabelValueType;
    SuperChargedOddCampaign?: LabelValueType;
    RiskSegmentUuids?: LabelValueType[];
    FranchiseIds?: LabelValueType[];
    freebet?: LabelValueType;
    feeds?: LabelValueType[];
    IsFirstPlayerBet?: LabelValueType;
    MarketUnionIds?: LabelValueType[];
    IsBetRequest?: LabelValueType;
  };
}

const initialState: BetListState = {
  bets: [],
  isAutoEnabled: false,
  filter: {},
};

const checkIsValid = (value: BetListRow, filter: BetListFilterModel): boolean => {
  let isValid = true;

  if (filter.sportsIds?.length) {
    if (value.Sport) {
      isValid = filter.sportsIds.some((s) => Number(s.value) === value.Sport);

      if (!isValid) return false;
    }
  }

  if (filter.playerID) {
    isValid = value.PlayerID === Number(filter.playerID);

    if (!isValid) return false;
  }

  if (filter.playerLogin) {
    isValid = value.Login === filter.playerLogin;

    if (!isValid) return false;
  }

  if (filter.eventID) {
    isValid = value.EventIds.includes(Number(filter.eventID));

    if (!isValid) return false;
  }

  if (filter.legs) {
    isValid = value.LegCount === Number(filter.legs);

    if (!isValid) return false;
  }

  if (filter.BetShopBonus) {
    isValid = value.BetShopBonus === Number(filter.BetShopBonus.value);

    if (!isValid) return false;
  }

  if (filter.tournamentIds?.length) {
    isValid = filter.tournamentIds.some((s) => Number(s.value) === value.Tournament);

    if (!isValid) return false;
  }

  if (Number(filter.type?.value) && Number(filter.type?.value) !== BET_LIST_LIVE_PREMATCH_EVENT_TYPES_ENUM.ALL) {
    isValid = Number(filter.type?.value) === value.TypeId;

    if (!isValid) return false;
  }

  if (filter.betBuilder && filter.betBuilder.value !== BETBUILDER_OPTIONS.ALL) {
    isValid = value.BetBuilderFilter === Number(filter.betBuilder.value);

    if (!isValid) return false;
  }

  if (filter.betID) {
    isValid = value.BetID === Number(filter.betID);

    if (!isValid) return false;
  }

  if (filter.barcode) {
    isValid = value.Barcode === filter.barcode;

    if (!isValid) return false;
  }

  if (filter.maxStakePercent) {
    isValid = value.MaxStakePercent >= Number(filter.maxStakePercent);

    if (!isValid) return false;
  }

  if (filter.isPartiallyCashedOut && filter.isPartiallyCashedOut.value !== CASHED_OUT_VALUES.ALL) {
    if (filter.isPartiallyCashedOut.value === CASHED_OUT_VALUES.PARTIALY_CASH_OUT) {
      isValid = value.IsPartiallyCashedOut;
      if (!isValid) return false;
    } else if (filter.isPartiallyCashedOut.value === CASHED_OUT_VALUES.CASH_OUT) {
      isValid = value.IsCashedOut;
      if (!isValid) return false;
    } else if (filter.isPartiallyCashedOut.value === CASHED_OUT_VALUES.NOT_CASH_OUT) {
      isValid = !value.IsCashedOut;
      if (!isValid) return false;
    }
  }

  if (filter.FranchiseIds?.length) {
    isValid = filter.FranchiseIds.some((f) => Number(f.value) === value.FranchiseId);
    if (!isValid) return false;
  }

  if (filter.RiskSegmentUuids?.length) {
    const riskSegmentsUUIds = value.RiskSegments.length > 0 ? value.RiskSegments.map((r) => r.Uuid) : [];

    isValid = filter.RiskSegmentUuids.some((f) => riskSegmentsUUIds.includes(f.value));
    if (!isValid) return false;
  }

  if (filter.SuperChargedOddCampaign) {
    isValid = filter.SuperChargedOddCampaign.value === value.SuperChargedOddSettingUuid;
    if (!isValid) return false;
  }

  if (filter.isTest && filter.isTest.value !== BET_LIST_IS_TEST_ENUM.ALL_BETS) {
    isValid = filter.isTest.value === BET_LIST_IS_TEST_ENUM.REAL_BETS
      ? value.IsTestBet === false
      : value.IsTestBet;
    if (!isValid) return false;
  }

  if (filter.IsFirstPlayerBet && filter.IsFirstPlayerBet.value !== IS_FIRST_PLAYER_BET_ENUM.ALL) {
    isValid = filter.IsFirstPlayerBet.value === IS_FIRST_PLAYER_BET_ENUM.ONLY_FIRST
      ? value.IsFirstPlayerBet === true
      : !!value.IsFirstPlayerBet;

    if (!isValid) return false;
  }

  if (filter.MinAmount) {
    isValid = value.Amount >= Number(filter.MinAmount);
    if (!isValid) return false;
  }

  if (filter.BetTypes && filter.BetTypes.some((type) => type.value !== BET_LIST_BET_TYPES_ALL.value)) {
    if (filter.BetTypes.some((type) => type.value === BET_LIST_BET_TYPES.SINGLES)) {
      isValid = value.BetTypeId === BetTypesEnum.Single;
    } else if (filter.BetTypes.some((type) => type.value === BET_LIST_BET_TYPES.MULTIBETS)) {
      isValid = value.BetTypeId === BetTypesEnum.Multiple;
    } else if (filter.BetTypes.some((type) => type.value === BET_LIST_BET_TYPES.SYSTEM)) {
      isValid = value.BetTypeId === BetTypesEnum.System;
    } else if (filter.BetTypes.some((type) => type.value === BET_LIST_BET_TYPES.FOLDS_AND_OTHER)) {
      isValid = value.BetTypeId !== BetTypesEnum.Single && value.BetTypeId !== BetTypesEnum.Multiple && value.BetTypeId !== BetTypesEnum.System;
    }
  }

  if (filter.freebet && filter.freebet.value !== BET_LIST_FREE_BET_TYPES.ALL) {
    if (filter.freebet.value === BET_LIST_FREE_BET_TYPES.BOTH) {
      isValid = value.FreeBetType === FreeBetResponseType.Freebet || value.FreeBetType === FreeBetResponseType.NoRiskBet;
    } else if (filter.freebet.value === BET_LIST_FREE_BET_TYPES.FREEBETS) {
      isValid = value.FreeBetType === FreeBetResponseType.Freebet;
    } else if (filter.freebet.value === BET_LIST_FREE_BET_TYPES.NO_FREE_BETS) {
      isValid = value.FreeBetType === FreeBetResponseType.NoRiskBet || value.FreeBetType === null;
    } else if (filter.freebet.value === BET_LIST_FREE_BET_TYPES.NO_RISK_FREEBETS) {
      isValid = value.FreeBetType === FreeBetResponseType.Freebet || value.FreeBetType === null;
    }
  }

  if (filter.feeds && filter.feeds.some((provider) => provider.value !== PROVIDER_TYPES.ALL)) {
    isValid = filter.feeds.some((f) => Number(f.value) === value.FeedId);
  }

  if (filter.MarketUnionIds?.length) {
    const selectedIds = filter.MarketUnionIds.map((f) => Number(f.value));
    const rowIds = value.MarketUnionIds?.map(Number) ?? [];

    isValid = rowIds.some((id) => selectedIds.includes(id));
    if (!isValid) return false;
  }

  if (filter.IpAddress) {
    isValid = value.Ip === filter.IpAddress;

    if (!isValid) return false;
  }

  if (filter.IsBetRequest && filter.IsBetRequest.value !== YES_NO_VALUES.ALL) {
    isValid = filter.IsBetRequest.value === YES_NO_VALUES.YES
      ? value.IsBetRequest
      : !value.IsBetRequest;

    if (!isValid) return false;
  }

  return isValid;
};

export const betListSlice = createSlice({
  name: 'betList',
  initialState,
  reducers: {
    addBet: (
      state: BetListState,
      action: PayloadAction<BetListRow>,
    ) => {
      if (state.isAutoEnabled) {
        const isValid = checkIsValid(action.payload, state.filter);

        if (isValid) {
          state.bets = [action.payload, ...state.bets];
        }
      }
    },
    clearBets: (state: BetListState) => {
      state.bets = [];
    },
    setAutoEnabled: (
      state: BetListState,
      action: PayloadAction<boolean>,
    ) => {
      state.isAutoEnabled = action.payload;
    },
    setBetListFilter: (
      state: BetListState,
      action: PayloadAction<BetListFilterModel>,
    ) => {
      const newFilter = action.payload;
      delete newFilter.dateClosed;
      delete newFilter.datePlaced;

      state.filter = newFilter;
    },
  },
});

export const {
  addBet, clearBets, setAutoEnabled, setBetListFilter,
} = betListSlice.actions;

export default betListSlice.reducer;
